import * as Yup from 'yup';
import * as helpers from '~/Utils/Helpers';

const schemaToValidation = Yup.object().shape(
  {
    tipo_pessoa: Yup.number().required('Selecione o tipo de pessoa'),
    nome: Yup.string().when('tipo_pessoa', {
      is: 0,
      then: Yup.string()
        .required('Informe o nome')
        .matches(/^[^\d]*$/, 'O nome não pode conter números'),
      otherwise: Yup.string().required('Informe o nome'),
    }),

    codigo_perfil_comprador: Yup.string()
      .required('Selecione um perfil do comprador')
      .test(
        'not-empty-or-zero',
        'Selecione um perfil do comprador',
        value => value !== '' && value !== '0'
      ),

    perfil_exterior_ddi: Yup.string().when(
      ['codigo_perfil_comprador'],
      (codigo_perfil_comprador, field) =>
        parseInt(codigo_perfil_comprador) === 4
          ? field.required('Informe o DDI (exterior)')
          : field
    ),
    perfil_exterior_telefone: Yup.string().when(
      ['codigo_perfil_comprador'],
      (codigo_perfil_comprador, field) =>
        parseInt(codigo_perfil_comprador) === 4
          ? field.required('Informe o Telefone (exterior)')
          : field
    ),
    perfil_exterior_endereco: Yup.string().when(
      ['codigo_perfil_comprador'],
      (codigo_perfil_comprador, field) =>
        parseInt(codigo_perfil_comprador) === 4
          ? field.required('Informe o Endereço (exterior)')
          : field
    ),
    perfil_exterior_pais: Yup.string().when(
      ['codigo_perfil_comprador'],
      (codigo_perfil_comprador, field) =>
        parseInt(codigo_perfil_comprador) === 4
          ? field.required('Informe o Pais (exterior)')
          : field
    ),
    email: Yup.string()
      .when('tel_celular', {
        is: tel_celular => !tel_celular || tel_celular.length === 0,
        then: Yup.string().required('Informe ao menos um contato'),
      })
      .email('Informe um email válido')
      .matches(
        /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
        'Informe um email no formato válido'
      ),
    tel_celular: Yup.string().test(
      'celular-valido',
      'Número de celular inválido',
      function (value) {
        if (!value) return true;
        const apenasNumeros = value.replace(/\D/g, '');
        if (apenasNumeros.length === 11) {
          const ddd = apenasNumeros.substring(0, 2);
          const primeiroDigito = apenasNumeros.charAt(2);
          return (
            parseInt(ddd) >= 11 && parseInt(ddd) <= 99 && primeiroDigito === '9'
          );
        }
        return false;
      }
    ),
    tel_residencial: Yup.string().test(
      'tel-residencial-valido',
      'Número de telefone residencial inválido',
      function (value) {
        if (!value) return true;
        const apenasNumeros = value.replace(/\D/g, '');
        if (apenasNumeros.length === 10) {
          value = helpers.format.celular(value || '');
          return /^(?:\(\d{2}\)\s?)?\d{4}-\d{4}$/.test(value);
        } else if (apenasNumeros.length === 11) {
          value = helpers.format.celular(value || '');
          return /^(?:\(\d{2}\)\s?)?\d{5}-\d{4}$/.test(value);
        }
        return false;
      }
    ),
    tel_comercial: Yup.string().test(
      'tel-comercial-valido',
      'Número de telefone comercial inválido',
      function (value) {
        if (!value) return true;
        const apenasNumeros = value.replace(/\D/g, '');
        if (apenasNumeros.length === 10) {
          value = helpers.format.celular(value || '');
          return /^(?:\(\d{2}\)\s?)?\d{4}-\d{4}$/.test(value);
        } else if (apenasNumeros.length === 11) {
          value = helpers.format.celular(value || '');
          return /^(?:\(\d{2}\)\s?)?\d{5}-\d{4}$/.test(value);
        }
        return false;
      }
    ),
    codigo_nacionalidade: Yup.string().required('Selecione uma nacionalidade'),
    cpf: Yup.string().when(
      ['TentouValidar', 'codigo_conta', 'is_documento_cin'],
      (TentouValidar, codigo_conta, is_documento_cin, field) =>
        is_documento_cin &&
          (TentouValidar ||
            codigo_conta ||
            (is_documento_cin && is_documento_cin.toString() === '1'))
          ? field.required('Informe o CPF/CIN')
          : field
    ),
    // RG é opcional: saiu dos documentos de venda e não é mais exigido no cadastro.
    rg: Yup.string().when('cpf', (cpf, field) =>
      field.test('rg-e-cpf', 'O RG não pode ser igual ao CPF', function (
        value
      ) {
        const { path, createError } = this;
        // Sem RG informado não há o que comparar com o CPF.
        if (!value) {
          return true;
        }
        if (
          (value || '')
            .toString()
            .replaceAll('.', '')
            .replaceAll('-', '') ===
          (cpf || '')
            .toString()
            .replaceAll('.', '')
            .replaceAll('-', '').length >
          0
        ) {
          return createError({
            path,
            message: 'O RG não pode ser igual ao CPF',
          });
        }
        return true;
      })
    ),
    codigo_profissao: Yup.string().when(
      ['TentouValidar', 'codigo_conta'],
      (TentouValidar, codigo_conta, field) =>
        TentouValidar || codigo_conta
          ? field.required('Selecione uma profissão')
          : field
    ),
    cep: Yup.string().when(
      ['TentouValidar', 'codigo_conta', 'estado', 'codigo_cidade', 'bairro', 'endereco', 'numero',],
      (TentouValidar, codigo_conta, estado, codigo_cidade, bairro, endereco, numero, field) => {
        const temCampoEnderecoPreenchido = estado || codigo_cidade || bairro || endereco || numero;
        if (TentouValidar || codigo_conta || temCampoEnderecoPreenchido) {
          return field.required('Informe o CEP');
        }
        return field;
      }
    ),
    estado: Yup.string().when(
      ['TentouValidar', 'codigo_conta'],
      (TentouValidar, codigo_conta, field) =>
        TentouValidar || codigo_conta
          ? field.required('Selecione um estado')
          : field
    ),
    codigo_cidade: Yup.string().when(
      ['TentouValidar', 'codigo_conta'],
      (TentouValidar, codigo_conta, field) =>
        TentouValidar || codigo_conta
          ? field.required('Selecione uma cidade')
          : field
    ),
    endereco: Yup.string().when(
      ['TentouValidar', 'codigo_conta'],
      (TentouValidar, codigo_conta, field) =>
        TentouValidar || codigo_conta
          ? field.required('Informe o endereço')
          : field
    ),
    numero: Yup.string().when(
      ['TentouValidar', 'codigo_conta'],
      (TentouValidar, codigo_conta, field) =>
        TentouValidar || codigo_conta
          ? field.required('Informe o número do imóvel')
          : field
    ),
    bairro: Yup.string().when(
      ['TentouValidar', 'codigo_conta'],
      (TentouValidar, codigo_conta, field) =>
        TentouValidar || codigo_conta
          ? field.required('Informe um bairro')
          : field
    ),
    // orgao_emissor_rg: Yup.string().when(
    //   ['TentouValidar', 'codigo_conta', 'tipo_pessoa'],
    //   (TentouValidar, codigo_conta, tipo_pessoa, field) =>
    //     (TentouValidar || codigo_conta) && parseInt(tipo_pessoa) === 0
    //       ? field.required('Informe o orgão emissor do RG')
    //       : field
    // ),
    // data_emissao_rg: Yup.string().when(
    //   ['TentouValidar', 'codigo_conta', 'tipo_pessoa'],
    //   (TentouValidar, codigo_conta, tipo_pessoa, field) =>
    //     (TentouValidar || codigo_conta) && parseInt(tipo_pessoa) === 0
    //       ? field.required('Informe a data de emissão do RG')
    //       : field
    // ),
    orgao_emissor_cnh: Yup.string().when(['cnh'], (cnh, field) =>
      cnh ? field.required('Informe o orgão emissor da cnh') : field
    ),
    estado_civil: Yup.string().when(
      ['TentouValidar', 'codigo_conta', 'tipo_pessoa'],
      (TentouValidar, codigo_conta, tipo_pessoa, field) =>
        (TentouValidar || codigo_conta) && parseInt(tipo_pessoa) === 0
          ? field.required('Selecione um estado civil')
          : field
    ),
    sexo: Yup.string().when(
      ['TentouValidar', 'codigo_conta', 'tipo_pessoa'],
      (TentouValidar, codigo_conta, tipo_pessoa, field) =>
        (TentouValidar || codigo_conta) && parseInt(tipo_pessoa) === 0
          ? field.required('Selecione um gênero')
          : field
    ),
    cep_correspondencia: Yup.string()
      .nullable()
      .when(['flg_endereco_correspondencia'], (flag, field) =>
        flag
          ? field
            .required('Informe o CEP de correspondência')
            .test(
              'cep-correspondencia-valido',
              'CEP de correspondência inválido',
              value => {
                if (value === null || value === undefined) return false;
                const digits = value.replace(/\D/g, '');
                return digits.length === 8;
              }
            )
          : field
      ),
    numero_correspondencia: Yup.string()
      .nullable()
      .when(['flg_endereco_correspondencia'], (flag, field) =>
        flag
          ? field.required('Informe o número do imóvel (correspondência)')
          : field
      ),
  },
  ['tel_celular', 'email'] // Resolve problema de ouvir o tel_celular e email
);
export default schemaToValidation;
